import React from "react";
import classNames from "classnames";
import { FormikValues, useFormikContext } from "formik";
import { OverwriteFieldConfig } from "../types.d";
import FormCheckboxField from "../checkboxField";
import { useCopies } from "shared/hooks/useCopies";
import {
  ConflictSuffix,
  ConflictType,
  DISPLAY_ONLY_REMOVED_VALUES_CONFLICT_TYPE_LIST
} from "shared/form/renderer/enums/Conflict";
import { hasVisibleConflictValue } from "shared/form/util";
import { useFormContextData } from "shared/form/context";
import difference from "lodash/difference";
import DateTimeHelper from "shared/utils/dateTimeHelper";
import EmailListChangedValues from "./components/emailListChangeValues";
import { PreviewImage } from "shared/form/core/assetUpload/defaultAssetPreview";
import { AssetConfig } from "shared/form/core/assetUpload/types";

function FormOverwriteField({ conflictType = ConflictType.TEXT, ...props }: OverwriteFieldConfig) {
  const formContextData = useFormContextData();
  const { values, initialValues }: { values: FormikValues; initialValues: FormikValues } =
    useFormikContext<FormikValues>();
  const copies = useCopies();
  const displayValue = React.useMemo(() => {
    if (typeof props.customDisplayValue === "function") {
      return props.customDisplayValue({
        value: values[`${props.forFieldName}${ConflictSuffix.VALUE}`]
      });
    } else if (props.customDisplayValue) {
      return props.customDisplayValue;
    } else {
      return values[`${props.forFieldName}${ConflictSuffix.VALUE}`];
    }
  }, [props, values]);

  const displayValuesForTags = React.useMemo(() => {
    if (DISPLAY_ONLY_REMOVED_VALUES_CONFLICT_TYPE_LIST.includes(conflictType)) {
      const lowerCasedValues = (values[props.forFieldName] || []).map((o: string) => o.toLowerCase());
      const lowerCasedConflictValues = (values[`${props.forFieldName}${ConflictSuffix.VALUE}`] || []).map((o: string) =>
        o.toLowerCase()
      );
      const removedValues = difference(lowerCasedConflictValues, lowerCasedValues) as Array<string>;
      const addedValues = difference(lowerCasedValues, lowerCasedConflictValues) as Array<string>;
      if (removedValues.length || addedValues.length) {
        const lowerCasedInitialValues = (initialValues[props.forFieldName] || []).map((o: string) => o.toLowerCase());
        return [
          ...removedValues.filter(o => !lowerCasedInitialValues.includes(o)),
          ...addedValues.filter(o => lowerCasedInitialValues.includes(o))
        ];
      }
    }
    return [];
  }, [conflictType, initialValues, props.forFieldName, values]);

  const hasConflict = React.useMemo(() => {
    if (DISPLAY_ONLY_REMOVED_VALUES_CONFLICT_TYPE_LIST.includes(conflictType)) {
      return Boolean(displayValuesForTags.length);
    }
    return hasVisibleConflictValue({ valuesObj: values, fieldName: props.forFieldName, conflictType });
  }, [conflictType, displayValuesForTags.length, props.forFieldName, values]);

  if (!hasConflict) {
    return null;
  }

  return (
    <React.Fragment>
      {conflictType === ConflictType.IMAGE && (
        <OverwriteFieldImage
          disabled={!!props.disabled}
          assetConfig={props.assetConfig}
          source={displayValue}
          altText={copies.get("conflict.image.of", {
            0: props.forFieldName
          })}
        />
      )}
      <div
        className={classNames(
          "overwriteField",
          props.className,
          conflictType === ConflictType.IMAGE ? "overwriteField--forImage" : ""
        )}>
        <div className="overwriteField_value">
          <React.Fragment>
            {!DISPLAY_ONLY_REMOVED_VALUES_CONFLICT_TYPE_LIST.includes(conflictType) && (
              <strong className="hasError">{copies.get("conflict.existing.value")}</strong>
            )}

            {conflictType === ConflictType.DATE &&
              DateTimeHelper.formatDate({ startDate: displayValue, option: props.formattingOptions })}
            {conflictType === ConflictType.TEXT && displayValue}
          </React.Fragment>

          {/** For Email list field: Showing changed values with added/remove tag */}
          {conflictType === ConflictType.EMAIL_LIST && (
            <EmailListChangedValues
              values={values[props.forFieldName] || []}
              conflictValues={values[`${props.forFieldName}${ConflictSuffix.VALUE}`] || []}
            />
          )}
        </div>

        {!formContextData.isReadOnly && (
          <FormCheckboxField
            label={copies.get("conflict.keep.existing.value")}
            name={`${props.forFieldName}${ConflictSuffix.CHECKBOX}`}
            disabled={props.disabled}
          />
        )}
      </div>
    </React.Fragment>
  );
}

export default FormOverwriteField;

function OverwriteFieldImage(props: { source: string; altText: string; assetConfig?: AssetConfig; disabled: boolean }) {
  return (
    <div className="imageUpload overwriteField--conflictImage">
      <div className="imageUpload_wrapper">
        <PreviewImage url={props.source} dimension={props.assetConfig?.dimension} disabled={props.disabled} />
      </div>
    </div>
  );
}
